<template>
  <v-dialog :model-value="modelValue" width="500" persistent>
    <v-card>
      <v-card-title class="bg-primary text-white text-h6">Set Payment Type</v-card-title>

      <v-card-text class="pt-6">
        <p class="text-body-1 mb-4">
          {{ promptText }}
          <br />
          <small class="text-grey-darken-1">This can always be updated by you (not the customer).</small>
        </p>

        <template v-if="stage === 'pre-order'">
          <v-switch
            v-model="localAllowOrder"
            :disabled="!versionCheckOk"
            hide-details
            label="Allow customer to place order"
            class="pt-0 mt-0"
          ></v-switch>
          <v-switch v-model="localFoc" hide-details label="Free of charge" class="pt-0 mt-2"></v-switch>
          <v-switch
            v-if="localAllowOrder && !localFoc"
            v-model="localAllowDeposit"
            hide-details
            label="Allow 50% deposit"
            class="pt-0 mt-2"
          ></v-switch>
        </template>

        <template v-else>
          <v-switch
            v-model="localFoc"
            :disabled="!focTogglableOnOrder"
            hide-details
            label="Free of charge"
            class="pt-0 mt-0"
          ></v-switch>
          <v-switch
            v-if="focTogglableOnOrder && !localFoc"
            v-model="localAllowDeposit"
            hide-details
            label="Allow 50% deposit"
            class="pt-0 mt-2"
          ></v-switch>
          <p v-if="!focTogglableOnOrder" class="text-caption text-grey-darken-1 mt-1 mb-0">
            FOC and deposit cannot be changed once payments have been made.
          </p>
        </template>

        <template v-if="showPaymentTypeRadio">
          <v-divider class="my-4" />
          <p class="text-subtitle-2 mb-1">Payment Method</p>
          <v-radio-group v-model="selectedMethod" hide-details class="mt-0">
            <v-radio label="Card" value="card" color="accent"></v-radio>
            <v-radio label="Bank Transfer" value="bacs" color="accent"></v-radio>
          </v-radio-group>
        </template>

        <p v-if="cardPaymentProcessing" class="text-caption text-red mt-4 mb-0">
          A card payment is currently being processed. Try again once it has settled.
        </p>

        <div v-if="paymentHistory.length > 0">
          <v-divider class="my-5" />
          <p class="text-subtitle-2 mb-2">Payment History</p>
          <div v-for="(entry, idx) in paymentHistory" :key="idx" class="text-body-2 mb-1">
            {{ entry.label }} of <b>{{ entry.amount }}</b
            >, paid on {{ entry.date }} by <b>{{ entry.method }}</b>
          </div>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="$emit('update:modelValue', false)">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :disabled="!canSave" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { db, functions } from '@/js/db'
import { doc, getDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'SetPaymentTypeDialog',
  emits: ['update:modelValue', 'saved'],
  props: {
    modelValue: { type: Boolean, default: false },
    quote: { type: Object, default: null },
    version: { type: Object, default: null },
    versionIndex: { type: Number, default: null },
    order: { type: Object, default: null }
  },
  data() {
    return {
      selectedMethod: 'card',
      localAllowOrder: false,
      localFoc: false,
      localAllowDeposit: false,
      saving: false
    }
  },
  computed: {
    stage() {
      if (!this.order) return 'pre-order'
      if (!this.order.payments || this.order.payments.length === 0) return 'pre-deposit'
      return 'post-deposit'
    },
    currentMethod() {
      if (this.order) return this.order.bankTransfer ? 'bacs' : 'card'
      if (this.version) return this.version.allowBankTransfer ? 'bacs' : 'card'
      return 'card'
    },
    versionCheckOk() {
      return !!(this.version && (this.version.versionCheckComplete || this.version.bypassVersionCheck))
    },
    focTogglableOnOrder() {
      // FOC can only flip if no payments have been recorded
      return !!(this.order && (!this.order.payments || this.order.payments.length === 0))
    },
    showPaymentTypeRadio() {
      if (this.stage === 'pre-order') return this.localAllowOrder && !this.localFoc
      return !this.localFoc
    },
    region() {
      return this.order?.region || this.quote?.region || 'uk'
    },
    currencySymbol() {
      if (this.region === 'us') return '$'
      if (this.region === 'eu') return '€'
      return '£'
    },
    expectedPaymentCount() {
      if (this.order) return this.order.allowDeposit ? 2 : 1
      if (this.version) return this.version.allowDeposit ? 2 : 1
      return 1
    },
    nextPaymentAmount() {
      if (this.stage === 'pre-deposit') {
        const total = this.order.total
        return this.expectedPaymentCount === 2 ? total / 2 : total
      }
      if (this.stage === 'post-deposit') {
        const paid = (this.order.payments || []).reduce((sum, p) => sum + (p.amount || 0), 0)
        return this.order.total - paid
      }
      return null
    },
    formattedNextAmount() {
      if (this.nextPaymentAmount == null) return ''
      return this.currencySymbol + this.$numeral(this.nextPaymentAmount).format('0,0.00')
    },
    promptText() {
      if (this.localFoc) return 'The order has been set to be FOC.'
      if (this.stage === 'pre-order') return 'Set initial payment type.'
      if (this.stage === 'pre-deposit') {
        const label = this.expectedPaymentCount === 2 ? '1st deposit payment' : 'full payment'
        return `Set ${label} type of ${this.formattedNextAmount}.`
      }
      return `Set remaining payment type of ${this.formattedNextAmount}.`
    },
    paymentHistory() {
      if (!this.order || !this.order.payments) return []
      const totalExpected = this.expectedPaymentCount
      return this.order.payments.map((p, idx) => {
        let label
        if (totalExpected === 1) {
          label = 'Full payment'
        } else if (idx === 0) {
          label = 'Deposit'
        } else {
          label = 'Balance'
        }
        // Method per payment is self-determining:
        let method
        if (p.method) {
          method = p.method
        } else if (p.charge && p.charge.payment_intent) {
          method = 'card'
        } else {
          method = 'bacs'
        }
        const dateStr = p.date ? this.$utils.momentFromTimestamp(p.date).format('Do MMM YYYY') : '—'
        return {
          label,
          amount: this.currencySymbol + this.$numeral(p.amount).format('0,0.00'),
          date: dateStr,
          method: method === 'bacs' ? 'Bank Transfer' : 'Card'
        }
      })
    },
    cardPaymentProcessing() {
      if (this.stage !== 'post-deposit') return false
      if (!this.order) return false
      const pendingID = this.order.pendingPaymentIntentID
      if (!pendingID) return false
      const first = this.order.payments && this.order.payments[0]
      return !(first && first.charge && first.charge.payment_intent === pendingID)
    },
    hasChanges() {
      if (this.stage === 'pre-order') {
        if (this.localAllowOrder !== !!this.version.allowOrder) return true
        if (this.localFoc !== !!this.version.foc) return true
        if (this.localAllowDeposit !== !!this.version.allowDeposit) return true
        if (this.localAllowOrder && !this.localFoc && this.selectedMethod !== this.currentMethod) return true
        return false
      }
      if (this.localFoc !== !!this.order.foc) return true
      if (this.focTogglableOnOrder && this.localAllowDeposit !== !!this.order.allowDeposit) return true
      if (!this.localFoc && this.selectedMethod !== this.currentMethod) return true
      return false
    },
    canSave() {
      return !this.saving && !this.cardPaymentProcessing && this.hasChanges
    }
  },
  watch: {
    modelValue(open) {
      if (open) {
        this.selectedMethod = this.currentMethod
        if (this.order) {
          this.localFoc = !!this.order.foc
          this.localAllowDeposit = !!this.order.allowDeposit
        } else if (this.version) {
          this.localAllowOrder = !!this.version.allowOrder
          this.localFoc = !!this.version.foc
          this.localAllowDeposit = !!this.version.allowDeposit
        }
      }
    }
  },
  methods: {
    async save() {
      this.saving = true
      try {
        const isBacs = this.selectedMethod === 'bacs'
        if (this.stage === 'pre-order') {
          // Re-read from firestore so we don't clobber other unsaved edits on the QuoteEdit page
          const quoteRef = doc(db, 'quotes', this.quote.id)
          const snap = await getDoc(quoteRef)
          const data = snap.data()
          const versions = data.versions
          const prevAllowOrder = !!versions[this.versionIndex].allowOrder
          const v = {
            ...versions[this.versionIndex],
            allowOrder: this.localAllowOrder,
            foc: this.localFoc,
            allowDeposit: this.localAllowDeposit,
            allowBankTransfer: this.localFoc ? false : isBacs
          }
          versions[this.versionIndex] = v

          // Maintain payableVersion mirror (matches existing toggleAllowOrder + onFocToggle logic)
          const payableVersion = data.payableVersion || {}
          if (v.allowOrder && !v.foc) {
            payableVersion[v.id] = {
              title: v.title,
              id: v.id,
              total: v.pricing?.total || 0
            }
          } else if (payableVersion[v.id]) {
            delete payableVersion[v.id]
          }

          await updateDoc(quoteRef, { versions, payableVersion })

          // Fire Baldwins stock check when allowOrder transitions off→on (matches toggleAllowOrder)
          if (!prevAllowOrder && v.allowOrder && !v.foc) {
            try {
              const fn = httpsCallable(functions, 'baldwinsStockCheck')
              await fn({ quoteID: this.quote.id, versionID: v.id })
            } catch (err) {
              console.error('Baldwins stock check failed:', err)
              this.$store.commit('openSnackbar', {
                snackbarText: 'Saved, but stock check notification failed',
                timeout: 5000
              })
            }
          }
        } else {
          const updates = {}
          if (this.localFoc !== !!this.order.foc) {
            updates.foc = this.localFoc
            // When un-FOCing, the order owes its full total; when FOCing, nothing is owed.
            updates.balanceOutstanding = !this.localFoc && (this.order.total || 0) > 0
          }
          const allowDepositChanged = this.focTogglableOnOrder && this.localAllowDeposit !== !!this.order.allowDeposit
          if (allowDepositChanged) {
            updates.allowDeposit = this.localAllowDeposit
          }
          // FOC orders are never bank transfer — clear the flag so no payment request is sent.
          updates.bankTransfer = this.localFoc ? false : isBacs
          if (Object.keys(updates).length > 0) {
            await updateDoc(doc(db, 'orders', this.order.id), updates)
          }
          // Mirror allowDeposit to version.allowDeposit so Summary.vue's "PAYABLE NOW" line stays in sync
          if (allowDepositChanged && this.order.quoteID && this.order.versionID) {
            const quoteRef = doc(db, 'quotes', this.order.quoteID)
            const snap = await getDoc(quoteRef)
            if (snap.exists()) {
              const versions = snap.data().versions || []
              const idx = versions.findIndex((v) => v.id === this.order.versionID)
              if (idx !== -1) {
                versions[idx] = { ...versions[idx], allowDeposit: this.localAllowDeposit }
                await updateDoc(quoteRef, { versions })
              }
            }
          }
        }
        this.$emit('saved', this.selectedMethod)
        this.$emit('update:modelValue', false)
      } catch (e) {
        this.$store.commit('openSnackbar', {
          snackbarText: 'Error saving: ' + e.message,
          timeout: 5000
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
